/*
 * Application configuration, permissions and settings
 *
 * Application.js
 *
 * (c) 2019 FSZ GmbH
 * All rights reserved.
 */

'use strict';

// external imports
///////////////////////////
//////////////////////////////////
//////////

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {ItemJsonAttributes} from '~/shared/src/ci/Item';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';

// frontend imports
import {PanelPropsWithPanelType} from '~/frontend/src/actions/panelManager/openPanel';

// backend imports
///////////////////////////
////////////////////////////////////////////////////////////
////////
//////////
//////////
////////
////////////////////////////////////////
//////////

export type ApplicationData = {
    key : string;
    name : string;
    item : ItemJsonAttributes;
    icon? : ItemJsonAttributes;
    darkIcon? : ItemJsonAttributes;
    image? : ItemJsonAttributes;
    darkImage? : ItemJsonAttributes;
    defaultPanelConfigs? : PanelPropsWithPanelType[];
    defaultPanelConfigActive : number;
}

type WaitForReady = Promise<boolean> & { resolve? : Function, reject? : Function };

/**
 *
 */
class Application {

    /**
     * Flag signaling the state of this (false on async (DB) activity)
     * @type {Boolean}
     * @default false
     */
    isReady : boolean = false;
    waitForReady : WaitForReady | null = null;

    key : string = '';
    name : string = '';
    defaultPanelConfigs : PanelPropsWithPanelType[] = [];
    defaultPanelConfigActive : number = 0;
    _itemId : number = 0;
    _iconItemId : number = 0;
    _darkIconItemId : number = 0;
    _imageItemId : number = 0;
    _darkImageItemId : number = 0;

    /**
     * @param {ApplicationData | string} applicationConfig
     * @param {string} [userId]
     */
    constructor( applicationConfig : ApplicationData, userId? : never )
    constructor( applicationConfig : string, userId : string )
    constructor( applicationConfig : ApplicationData | string, userId : string | undefined ) {
        if ( typeof applicationConfig === 'object' ) {
            this.fromJSON( applicationConfig );
        }
        else if ( userId ) {
            this.key = applicationConfig;

            this.createWaitForReady();

            this.loadFromDB( userId ).catch( ( e ) => {
                error( e instanceof Error ? e.message : e );
            } );
        }
    }

    toJSON() : ApplicationData {
        return {
            key                     : this.key,
            name                    : this.name,
            item                    : this.item?.toJSON() ?? {} as ItemJsonAttributes,
            icon                    : this.iconItem?.toJSON(),
            darkIcon                : this.darkIconItem?.toJSON(),
            image                   : this.imageItem?.toJSON(),
            darkImage               : this.darkImageItem?.toJSON(),
            defaultPanelConfigs     : this.defaultPanelConfigs,
            defaultPanelConfigActive: this.defaultPanelConfigActive
        };
    }

    fromJSON( json : ApplicationData ) {
        this.key = json.key;
        this.name = json.name;

        if ( json.item ) {
            const item = new Item( json.item, this.itemContext, 'CFG' );
            this._itemId = item.id;
        }

        if ( json.icon ) {
            const item = new Item( json.icon, this.itemContext, 'CFG' );
            this._iconItemId = item.id;
        }
        if ( json.darkIcon ) {
            const item = new Item( json.darkIcon, this.itemContext, 'CFG' );
            this._darkIconItemId = item.id;
        }
        if ( json.image ) {
            const item = new Item( json.image, this.itemContext, 'CFG' );
            this._imageItemId = item.id;
        }
        if ( json.darkImage ) {
            const item = new Item( json.darkImage, this.itemContext, 'CFG' );
            this._darkImageItemId = item.id;
        }

        this.defaultPanelConfigs = json.defaultPanelConfigs ?? [];
        this.defaultPanelConfigActive = json.defaultPanelConfigActive ?? 0;

        this.isReady = true;
    }

    /**
     * Creates and returns a promise which will be resolved if the instance is ready.
     *
     * @return {Promise<void>}
     */
    async ifReady() : Promise<boolean> {
        if ( this.isReady ) {
            return true;
        }

        if ( this.waitForReady ) {
            return this.waitForReady;
        }

        return this.isReady;
    }

    /**
     * Set ready state
     * @param value
     */
    setReady() {
        this.isReady = true;

        if ( this.waitForReady ) {
            this.waitForReady.resolve?.( this.isReady );
            this.waitForReady = null;
        }
    }

    /**
     * Set ready state by rejecting the waitForReady promise
     */
    setFailed( e : any ) {
        this.isReady = false;

        if ( this.waitForReady ) {
            this.waitForReady.reject?.( e );
            this.waitForReady = null;
        }
    }


    /*** misc *****************************************************************************************************************************/

    clone() : Application {
        const retVal = new Application( this.toJSON() );

        return retVal;
    }

    get item() : Item | undefined {
        return global.data[ this.itemContext ]?.items[ this._itemId ];
    }

    get imageItem() : Item | undefined {
        return global.data[ this.itemContext ]?.items[ this._imageItemId ];
    }

    get darkImageItem() : Item | undefined {
        return global.data[ this.itemContext ]?.items[ this._darkImageItemId ];
    }

    get iconItem() : Item | undefined {
        return global.data[ this.itemContext ]?.items[ this._iconItemId ];
    }

    get iconItemCssClass() : string {
        const iconItem = global.data[ this.itemContext ]?.items[ this._iconItemId ];

        let retVal : string = 'fas fa-window-maximize';

        if ( iconItem ) {
            const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'CssClass' )?.id,
                  property       = propertyTypeId ? iconItem.properties.get( propertyTypeId ) : undefined;

            if ( property?.value ) {
                retVal = property.value as string;
            }
            else {
                retVal = getIconCssClassName( 'byRef', iconItem.key );
            }
        }

        return retVal;
    }

    get darkIconItem() : Item | undefined {
        return global.data[ this.itemContext ]?.items[ this._darkIconItemId ];
    }

    get darkIconItemCssClass() : string {
        const iconItem = global.data[ this.itemContext ]?.items[ this._darkIconItemId ];

        let retVal : string = 'fas fa-window-maximize';

        if ( iconItem ) {
            const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'CssClass' )?.id,
                  property       = propertyTypeId ? iconItem.properties.get( propertyTypeId ) : undefined;

            if ( property?.value ) {
                retVal = property.value as string;
            }
            else {
                retVal = getIconCssClassName( 'byRef', iconItem.key );
            }
        }
        else {
            retVal = this.iconItemCssClass;
        }

        return retVal;
    }

    get title() : string | undefined {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Title' )?.id,
              property       = propertyTypeId && this.item ? this.item.properties.get( propertyTypeId ) : undefined;

        let retVal : string | undefined;

        if ( property?.value ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    get windowTitle() : string | undefined {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'WindowTitle' )?.id,
              property       = propertyTypeId && this.item ? this.item.properties.get( propertyTypeId ) : undefined;

        let retVal : string | undefined;

        if ( property?.value ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    get color() : string | undefined {
        const propertyType = metaModel[ 'CFG' ].getPropertyType( 'TitleBarColor' ),
              property     = propertyType && this.item ? this.item.properties.get( propertyType.id ) : undefined;

        let retVal : string | undefined;

        if ( property?.value ) {
            retVal = String( property.value );
        }

        return retVal;
    }

    /**
     * Getter for the image data of the application image
     */
    get image() : string | undefined {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Content' )?.id,
              enumProperty   = propertyTypeId && this.imageItem ? this.imageItem.enumProperties.get( propertyTypeId ) : undefined;

        let retVal : string | undefined;

        if ( enumProperty?.properties.has( '2x' ) ) {
            retVal = String( enumProperty.properties.get( '2x' )!.value );
        }
        else if ( enumProperty?.properties.has( '1x' ) ) {
            retVal = String( enumProperty.properties.get( '1x' )!.value );
        }

        return retVal;
    }

    /**
     * Getter for the dark image data of the application image
     */
    get darkImage() : string | undefined {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Content' )?.id,
              enumProperty   = propertyTypeId && this.darkImageItem ? this.darkImageItem.enumProperties.get( propertyTypeId ) : undefined;

        let retVal : string | undefined;

        if ( enumProperty?.properties.has( '2x' ) ) {
            retVal = String( enumProperty.properties.get( '2x' )!.value );
        }
        else if ( enumProperty?.properties.has( '1x' ) ) {
            retVal = String( enumProperty.properties.get( '1x' )!.value );
        }

        return retVal;
    }

    /**
     * Getter for the image data of the application icon
     */
    get icon() : string | undefined {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Content' )?.id,
              enumProperty   = propertyTypeId && this.iconItem ? this.iconItem.enumProperties.get( propertyTypeId ) : undefined;

        let retVal : string | undefined;

        if ( enumProperty?.properties.has( '2x' ) ) {
            retVal = String( enumProperty.properties.get( '2x' )!.value );
        }
        else if ( enumProperty?.properties.has( '1x' ) ) {
            retVal = String( enumProperty.properties.get( '1x' )!.value );
        }

        return retVal;
    }

    /**
     * Getter for the image data of the application icon
     */
    get darkIcon() : string | undefined {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Content' )?.id,
              enumProperty   = propertyTypeId && this.darkIconItem ? this.darkIconItem.enumProperties.get( propertyTypeId ) : undefined;

        let retVal : string | undefined;

        if ( enumProperty?.properties.has( '2x' ) ) {
            retVal = String( enumProperty.properties.get( '2x' )!.value );
        }
        else if ( enumProperty?.properties.has( '1x' ) ) {
            retVal = String( enumProperty.properties.get( '1x' )!.value );
        }

        return retVal;
    }

    /**
     * Getter for the file name of the application icon
     */
    get iconFileName() : string {
        return `application_icon_${this.key.replace( /:/g, '_' ).toLowerCase()}.ico`;
    }

    /**
     * Getter for the file name of the application icon
     */
    get darkIconFileName() : string {
        return `application_icon_${this.key.replace( /:/g, '_' ).toLowerCase()}_dark.ico`;
    }

    get schema() : Schema {
        const propertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Schema' )?.id,
              property       = propertyTypeId && this.item ? this.item.properties.get( propertyTypeId ) : undefined;

        let retVal : Schema = global.defaultSchema;

        if ( property?.value ) {
            retVal = String( property.value ).toUpperCase() as Schema;
        }

        return retVal;
    }

    get itemContext() : string {
        return `application_${this.key}`;
    }


    /*** private common *******************************************************************************************************************/

    private createWaitForReady() {
        let resolveFnc : Function | undefined,
            rejectFnc  : Function | undefined;

        this.waitForReady = new Promise<boolean>( ( resolve, reject ) => {
            resolveFnc = resolve;
            rejectFnc  = reject;
        } );

        this.waitForReady.resolve = resolveFnc;
        this.waitForReady.reject  = rejectFnc;
    }

    /*** frontend *************************************************************************************************************************/

///////////////////////////////

//////////////


    /*** backend **************************************************************************************************************************/

///////////////////////////////

///////
///////////////////////////////////////////////////
///////
/////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////

/////////////
///////////////////////////////////////////////
//////////////////////////////////
///////////////////////////////
//////////////////////////////
//////////////

///////////////////////////////////////////////////
/////////
/////////////////////
////////////////////////////////
////////////////////////////////////////////////////////
////////////////////////
/////////

/////////////
////////////////////////////////////////////
/////////
/////////////////////
///////////////////////////////////////////////////////
/////////

/////////////
////////////////////////////////////////////////
/////////
/////////////////////
///////////////////////////////////////////////////////
/////////

/////////////
/////////////////////////////////////////////
/////////
/////////////////////
///////////////////////////////////////////////////////
/////////

/////////////
/////////////////////////////////////////////////
/////////
/////////////////////
///////////////////////////////////////////////////////
/////////

/////////////
///////////////////////////////////////////////////////////////
/////////
/////////////////////
///////////////////////////////////////////////////////
/////////

////////////////////////
/////

///////
////////////////////////////////////////////////
///////
///////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////

/////////////
////////////////////////////////////////////////////
/////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////////
///////////////////////////////////////////////////
/////////////
//////////////////
///////////////////////////////////////////////////////////
/////////////
/////////
/////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
/////////////////////////////////////////////////////
///////
////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////

/////////////
/////////////////////////////////////////////////
//////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
///////////////////////////
////////////////////

///////////////////////////////////
////////////////////////////////////////////////
/////////////
//////////////////
/////////////////////////////////////////////////////////////////////
/////////////
/////////
/////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
/////////////////////////////////////////////////////
///////
////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////

/////////////
/////////////////////////////////////////////////
//////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
///////////////////////////
////////////////////

///////////////////////////////////
////////////////////////////////////////////////////
/////////////
//////////////////
//////////////////////////////////////////////////////////////////////////
/////////////
/////////
/////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
//////////////////////////////////////////////////////
///////
/////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////

/////////////
/////////////////////////////////////////////////
//////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////
/////////////////////////////////////////////////
/////////////
//////////////////
//////////////////////////////////////////////////////////////////////
/////////////
/////////
/////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
//////////////////////////////////////////////////////
///////
/////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////

/////////////
/////////////////////////////////////////////////
//////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
///////////////////////////
////////////////////

///////////////////////////////////
/////////////////////////////////////////////////////
/////////////
//////////////////
///////////////////////////////////////////////////////////////////////////
/////////////
/////////
/////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
////////////////////////////////////////////////////
///////
////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////

//////////////////////////////////////
//////////////////////////////////////////

/////////////
/////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////
//////////////////////
/////////////////
/////////////////////////////////////
//////////////////////////
/////////////////
//////////////

///////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////
/////////////
//////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
/////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////
///////////////////////////////////
///////////////////////////////////////////////
////////////////////////////////
//////////////////////////////////////////
////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////
/////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////
/////////////////////////////////////////////////////////
////////////////////////////////////////

//////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////

/////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////

/////////////////////////////////////////////////////////////////
////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////
////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////
///////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////
///////////////////////////////////
////////////////////////////////////////
///////////////////////////////////////////////
///////////////////////////////////
///////////////////////////////
////////////////////////////

///////////////////////////////////
////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
/////////////////////////////
/////////////////////////
/////////////////////
/////////////////
/////////////
/////////
/////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////
/////

///////
/////////////////////////////////////////////////////////
///////
///////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////

////////////////////////////////////////

/////////////////////////////
////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////
/////////////////////
/////////////////
/////////////

////////////////////////////////////////////
////////////////////////////////////////////////////////////
/////////////
/////////

//////////////////////
/////

//////////////
}

export default Application;
